// components/ModelGalleryModal.tsx
import React, { useMemo } from 'react';
import { X } from '@/components/ui/lucide-memo';
import { useKernelStore } from '@/kernel/kernelStores';
import {
  estimateStudioModelCredits,
  StudioModelId,
  STUDIO_MODEL_REGISTRY,
} from '#lib/model-registry';
import { getModelGalleryCopy } from '#lib/modelGalleryCopy';

interface ModelGalleryModalPayload {
  modelIds?: string[];
  selectedModelId?: string | null;
  category?: string | null;
  subCategory?: string | null;
}

interface ModelGalleryModalProps {
  isOpen: boolean;
  payload?: ModelGalleryModalPayload | null;
  onClose: () => void;
}

const LIFECYCLE_BADGE: Record<'flagship' | 'active' | 'legacy' | 'experimental', string> = {
  flagship: 'Flagship',
  active: 'Active',
  legacy: 'Legacy',
  experimental: 'Experimental',
};

const LIFECYCLE_BADGE_FR: Record<'flagship' | 'active' | 'legacy' | 'experimental', string> = {
  flagship: 'Flagship',
  active: 'Actif',
  legacy: 'Legacy',
  experimental: 'Experimental',
};

const stopPropagation = (event: React.MouseEvent) => event.stopPropagation();

const formatAverageDuration = (averageSeconds: number, lang: string, avgSuffix: string) => {
  const roundedSeconds = Math.ceil(averageSeconds);
  const minutes = Math.floor(roundedSeconds / 60);
  const seconds = roundedSeconds % 60;
  if (minutes > 0) {
    return lang === 'fr'
      ? `~${minutes} min ${seconds}s ${avgSuffix}`
      : `~${minutes}m ${seconds}s ${avgSuffix}`;
  }
  return `~${roundedSeconds}s ${avgSuffix}`;
};

const ModelGalleryModal: React.FC<ModelGalleryModalProps> = ({ isOpen, payload, onClose }) => {
  const modelParams = useKernelStore((state) => state.modelParams);
  const organisationId = useKernelStore((state) => state.userContext.organisationId);
  const lang = useKernelStore((state) => state.language);

  const ui = useMemo(() => {
    if (lang === 'fr') {
      return {
        title: 'Galerie des modeles',
        subtitle: (count: number) =>
          `${count} modele${count > 1 ? 's' : ''} selectionnable${count > 1 ? 's' : ''} depuis la liste actuelle. Chaque ligne explique pourquoi le choisir face a Nano Banana 2.`,
        empty: 'Aucun modele selectionnable dans ce contexte.',
        tradeOff: 'Compromis',
        close: 'Fermer',
        notAvailable: 'n/a',
        avgNotAvailable: 'moy n/a',
        creditSingular: 'credit',
        creditPlural: 'credits',
        perGeneration: '/ gen',
        avgSuffix: 'moy',
        detailsPending: 'Details en attente.',
        lifecycleBadges: LIFECYCLE_BADGE_FR,
      };
    }
    return {
      title: 'Model Gallery',
      subtitle: (count: number) =>
        `${count} selectable model${count > 1 ? 's' : ''} from the current list. Each line explains why to pick it over Nano Banana 2.`,
      empty: 'No selectable models available in this context.',
      tradeOff: 'Trade-off',
      close: 'Close',
      notAvailable: 'n/a',
      avgNotAvailable: 'avg n/a',
      creditSingular: 'credit',
      creditPlural: 'credits',
      perGeneration: '/ gen',
      avgSuffix: 'avg',
      detailsPending: 'Details pending.',
      lifecycleBadges: LIFECYCLE_BADGE,
    };
  }, [lang]);

  const requestedModelIds = useMemo(() => {
    if (!Array.isArray(payload?.modelIds)) return [];
    const seen = new Set<string>();
    const orderedUnique: string[] = [];
    for (const rawId of payload.modelIds) {
      const id = typeof rawId === 'string' ? rawId.trim() : '';
      if (!id || seen.has(id)) continue;
      seen.add(id);
      orderedUnique.push(id);
    }
    return orderedUnique;
  }, [payload?.modelIds]);

  const modelCards = useMemo(() => {
    const lifecyclePriority: Record<'flagship' | 'active' | 'legacy' | 'experimental', number> = {
      flagship: 0,
      active: 20,
      legacy: 30,
      experimental: 40,
    };

    const explicitPriority: Record<string, number> = {
      'nano-banana-2': 0,
      'nano-banana-2.1': 0,
      'grok-imagine-2': 5,
      'muse-image': 6,
      'flux-3-image': 7,
      'grok-imagine-image': 10,
      'flux-2-klein-4b': 11,
    };

    const originalOrder = new Map(requestedModelIds.map((id, index) => [id, index]));
    const isVideoCategory = payload?.category === 'Video';
    const selectedModelId =
      typeof payload?.selectedModelId === 'string' && payload.selectedModelId.trim().length > 0
        ? payload.selectedModelId.trim()
        : null;
    const selectedModelParams =
      selectedModelId &&
      typeof modelParams[selectedModelId] === 'object' &&
      modelParams[selectedModelId] !== null
        ? (modelParams[selectedModelId] as unknown as Record<string, unknown>)
        : null;
    const baselineDuration =
      selectedModelParams && typeof selectedModelParams.duration === 'number' && Number.isFinite(selectedModelParams.duration)
        ? selectedModelParams.duration
        : null;
    const baselineResolution =
      selectedModelParams && typeof selectedModelParams.resolution === 'string'
        ? selectedModelParams.resolution
        : null;
    const baselineGenerateAudio =
      selectedModelParams && typeof selectedModelParams.generate_audio === 'boolean'
        ? selectedModelParams.generate_audio
        : null;
    const costFormatter = new Intl.NumberFormat(lang === 'fr' ? 'fr-FR' : 'en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

    const cards = requestedModelIds
      .map((id) => {
        if (!(id in STUDIO_MODEL_REGISTRY)) return null;
        const definition = STUDIO_MODEL_REGISTRY[id as StudioModelId];
        if (!definition) return null;
        const profile = definition.comparisonProfile;
        const lifecycle = profile?.lifecycle ?? 'active';
        const localizedCopy = getModelGalleryCopy(id, lang === 'fr' ? 'fr' : 'en');
        const paramsForEstimate =
          typeof modelParams[id] === 'object' && modelParams[id] !== null
            ? { ...(modelParams[id] as unknown as Record<string, unknown>) }
            : ({} as Record<string, unknown>);
        if (isVideoCategory) {
          if (baselineDuration !== null) paramsForEstimate.duration = baselineDuration;
          if (baselineResolution !== null) paramsForEstimate.resolution = baselineResolution;
          if (baselineGenerateAudio !== null) paramsForEstimate.generate_audio = baselineGenerateAudio;
        }
        const estimatedCost = estimateStudioModelCredits(
          id,
          paramsForEstimate,
          { organisationId },
        );
        const roundedCost =
          typeof estimatedCost === 'number' && Number.isFinite(estimatedCost)
            ? Math.round(estimatedCost * 100) / 100
            : null;
        const formattedCost = typeof roundedCost === 'number' ? costFormatter.format(roundedCost) : null;
        return {
          id,
          name: definition.name,
          lifecycle,
          positioning:
            localizedCopy?.reason ||
            profile?.positioning ||
            definition.powerHint ||
            ui.detailsPending,
          limitation:
            localizedCopy?.tradeOff ||
            profile?.knownLimitations?.[0] ||
            profile?.avoidFor?.[0] ||
            null,
          averageGenerationSeconds:
            typeof definition.averageGenerationSeconds === 'number' && Number.isFinite(definition.averageGenerationSeconds)
              ? definition.averageGenerationSeconds
              : null,
          estimatedCostLabel: formattedCost
            ? `${formattedCost} ${Math.abs((roundedCost ?? 0) - 1) < 1e-9 ? ui.creditSingular : ui.creditPlural} ${ui.perGeneration}`
            : null,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);

    cards.sort((a, b) => {
      const aPriority = explicitPriority[a.id] ?? lifecyclePriority[a.lifecycle];
      const bPriority = explicitPriority[b.id] ?? lifecyclePriority[b.lifecycle];
      if (aPriority !== bPriority) return aPriority - bPriority;
      return (originalOrder.get(a.id) ?? 0) - (originalOrder.get(b.id) ?? 0);
    });

    return cards;
  }, [
    payload?.category,
    payload?.selectedModelId,
    lang,
    modelParams,
    requestedModelIds,
    organisationId,
    ui.creditPlural,
    ui.creditSingular,
    ui.detailsPending,
    ui.perGeneration,
  ]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-neutral-800 text-white rounded-lg shadow-xl w-full max-w-5xl relative flex flex-col max-h-[90vh]"
        onClick={stopPropagation}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-neutral-400 hover:text-white z-10"
          aria-label="Close model gallery modal"
        >
          <X size={20} />
        </button>

        <div className="p-6 overflow-y-auto custom-scrollbar flex-grow">
          <h2 className="text-xl font-semibold mb-2">{ui.title}</h2>
          <p className="text-sm text-neutral-400 mb-6">
            {ui.subtitle(modelCards.length)}
          </p>

          <div className="rounded-lg border border-neutral-700 bg-neutral-700/25 overflow-hidden">
            <div className="max-h-[56vh] overflow-y-auto custom-scrollbar">
              {modelCards.length === 0 ? (
                <div className="px-4 py-6 text-sm text-neutral-400">
                  {ui.empty}
                </div>
              ) : (
                modelCards.map((model) => (
                  <div
                    key={model.id}
                    className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 border-b border-neutral-700/70 px-4 py-4 last:border-b-0"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="truncate text-sm font-semibold text-white">{model.name}</p>
                        <span className="rounded border border-neutral-600 bg-neutral-700/70 px-2 py-0.5 text-[10px] text-neutral-300">
                          {ui.lifecycleBadges[model.lifecycle]}
                        </span>
                      </div>
                      <p className="mt-1 text-sm leading-5 text-neutral-200">{model.positioning}</p>
                      {model.limitation ? (
                        <p className="mt-0.5 text-xs text-neutral-500">{ui.tradeOff}: {model.limitation}</p>
                      ) : null}
                    </div>
                    <div className="shrink-0 min-w-[116px] text-right">
                      <p className="text-sm font-medium text-neutral-200">{model.estimatedCostLabel || ui.notAvailable}</p>
                      <p className="mt-1 text-xs text-neutral-400">
                        {typeof model.averageGenerationSeconds === 'number'
                          ? formatAverageDuration(model.averageGenerationSeconds, lang, ui.avgSuffix)
                          : ui.avgNotAvailable}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-neutral-700 bg-neutral-800 flex-shrink-0">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-neutral-300 bg-neutral-600 hover:bg-neutral-500 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-neutral-800 focus:ring-[#B3D9FF]"
            >
              {ui.close}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ModelGalleryModal);
